import React from 'react';
import { View, Text, Image } from 'react-native-web';
import moment from 'moment';
import Ripple from 'react-ripples';
import * as Animatable from 'react-native-animatable';

// Assets
import { ReactComponent as Logo } from './images/logo_vendor_landscape_white.svg';

// Utils
import { getLogoPhoto } from './utils/util_truck_helpers';

// Configs
import GLOBAL from './configs/config_global';
import { STATE_KEYS, useGlobalState } from './configs/config_state';

// Navigation
import * as RootNavigation from './navigation/RootNavigation';
import routeKeys from './navigation/RouteKeys';

export default function AppTabBar() {
    // region Menu Items
    const MENU_ITEMS = [
        {
            route: routeKeys.PROFILE_STACK,
            name: 'Profile',
            icon: require('./images/iconGlyphSearch.png')
        },
        {
            route: routeKeys.DASHBOARD_STACK,
            name: 'Dashboard',
            icon: require('./images/iconLineDashboard.png')
        },
        {
            route: routeKeys.ORDER_STACK,
            name: 'Orders',
            icon: require('./images/iconLineReceipt.png')
        },
        {
            route: routeKeys.CATERING_STACK,
            name: 'Catering',
            icon: require('./images/iconLineCatering.png')
        },
        {
            route: routeKeys.CHAT_STACK,
            name: 'Chat',
            icon: require('./images/iconLineChat.png')
        },
        {
            route: routeKeys.RESOURCES_STACK,
            name: 'Resources',
            icon: require('./images/iconLineResources.png')
        },
        {
            route: routeKeys.MORE_STACK,
            name: 'More',
            icon: require('./images/iconLineMenu.png')
        }
    ]
    // endregion

    // region Global State
    const [truckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [currentTabStack, setCurrentTabStack] = useGlobalState(STATE_KEYS.CURRENT_TAB_STACK);
    const [appMainView] = useGlobalState(STATE_KEYS.MAIN_VIEW);
    const [inScreenModalShowing] = useGlobalState(STATE_KEYS.IN_SCREEN_MODAL_SHOWING);
    const [isAddingAccount] = useGlobalState(STATE_KEYS.ADDING_NEW_ACCOUNT);
    // endregion

    // region Render Menu Button
    const renderMenuButton = (item) => {
        const isChosen = currentTabStack === item.route;
        return(
            <Ripple key={item.route} color={'rgba(255, 255, 255, 0.5)'}>
                <button
                    onClick={() => {
                        if (!isChosen) {
                            setCurrentTabStack(item.route);
                            RootNavigation.jumpTo(item.route);
                            appMainView?.fadeZoom(600);
                        }
                    }}
                    style={{
                        display: 'flex', width: '100%', paddingLeft: 32, paddingRight: 32, paddingTop: 24, paddingBottom: 24,
                        flexDirection: 'row', alignItems: 'center', backgroundColor: 'transparent', border: 'none'
                    }}>
                    {/* Icon */}
                    <Image
                        source={item.icon}
                        style={{ width: 24, height: 24, tintColor: isChosen ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_GRAY }}
                        resizeMode={'contain'} />

                    {/* Text */}
                    <Text style={{ color: isChosen ? 'white' : GLOBAL.COLOR.TEXT_GRAY, fontSize: 16, fontWeight: isChosen ? 'bold' : 'normal', marginLeft: 16 }}>
                        {item.name}
                    </Text>
                </button>
            </Ripple>
        );
    };
    // endregion

    // region Render User Account Button
    const renderUserAccountButton = () => {
        const stackName = routeKeys.ACCOUNT_SETTINGS_STACK;
        const isChosen = currentTabStack === stackName;
        return(
            <Ripple color={'rgba(255, 255, 255, 0.5)'}>
                <button
                    onClick={() => {
                        if (!isChosen) {
                            setCurrentTabStack(stackName);
                            RootNavigation.jumpTo(stackName);
                            appMainView?.fadeZoom(600);
                        }
                    }}
                    style={{ display: 'flex', width: '100%', border: 'none', backgroundColor: isChosen ? GLOBAL.COLOR.MAIN : 'transparent' }}>
                    <View style={{
                        width: '100%', flexDirection: 'row', alignItems: 'center',
                        borderColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY, borderTopWidth: 1, borderBottomWidth: 1,
                        paddingTop: 24, paddingBottom: 24, paddingLeft: 32, paddingRight: 32
                    }}>
                        {/* Avatar */}
                        <Image
                            source={{ uri: getLogoPhoto(truckData, null) }}
                            style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}
                            resizeMode={'cover'}
                        />

                        {/* Name and Description */}
                        <View style={{ flex: 1, backgroundColor: 'transparent', marginLeft: 20 }}>
                            {/* Name */}
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: 'bold', userSelect: 'none' }}>
                                {GLOBAL.DATA.USER?.name}
                            </Text>


                            {/* Description */}
                            <Text style={{ color: isChosen ? 'white' : GLOBAL.COLOR.TEXT_GRAY, fontSize: 14, marginTop: 8 }}>
                                Account Settings
                            </Text>
                        </View>
                    </View>
                </button>
            </Ripple>
        );
    }
    // endregion

    // region MAIN RENDER
    const logoWidth = 250;
    return(
        !isAddingAccount &&
        <Animatable.View
            animation={'slideInLeft'}
            style={{ backgroundColor: 'black', width: '15%', maxWidth: 600, minWidth: 300, height: '100%' }}>
            {/* Logo */}
            <Logo style={{ width: logoWidth, height: (34 / 156) * logoWidth, marginTop: 36, alignSelf: 'center' }}/>

            {/* Main Content */}
            <View style={{ flex: 1, paddingTop: 32 }}>
                {/* Menu Items */}
                {MENU_ITEMS.map((item) => renderMenuButton(item))}

                {/* Blank Space */}
                <View style={{ flex: 1, backgroundColor: 'transparent' }}/>

                {/* User Account */}
                {renderUserAccountButton()}
            </View>

            {/* Copyright Text */}
            <Text style={{ color: 'white', fontSize: 12, textAlign: 'center', alignSelf: 'center', marginVertical: 16 }}>
                {moment().year()} © Truckster
            </Text>

            {/* In-Screen Modal Backdrop */}
            <Animatable.View
                pointerEvents={inScreenModalShowing ? 'auto' : 'none'}
                animation={inScreenModalShowing ? 'fadeIn' : 'fadeOut'}
                duration={400}
                style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
            />
        </Animatable.View>
    );
    // endregion
}
